import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState, type FormEvent } from "react";
import { useTranslations } from "next-intl";
import { Button } from "@navi-soft/ui/button";
import { HintAsterisk } from "@navi-soft/ui/hint-asterisk";
import { TextField } from "@navi-soft/ui/text-field";
import { useToast } from "@navi-soft/ui/toast";
import {
  AuthCard,
  authErrorClassName,
  authFieldClassName,
  authFormClassName,
  authMutedClassName,
} from "@/components/auth-card";
import { setPassword } from "@/lib/api";

/** Query params must stay strings (Zitadel IDs are > Number.MAX_SAFE_INTEGER). */
function searchString(value: unknown): string {
  if (typeof value === "string") return value;
  if (typeof value === "number" && Number.isFinite(value)) return String(value);
  return "";
}

export const Route = createFileRoute("/invite")({
  validateSearch: (search: Record<string, unknown>) => ({
    userID: searchString(search.userID ?? search.userId),
    code: searchString(search.code),
    orgID: searchString(search.orgID ?? search.orgId),
    mode: search.mode === "reset" ? ("reset" as const) : ("invite" as const),
  }),
  component: InvitePage,
});

function InvitePage() {
  const t = useTranslations("app");
  const { error: toastError } = useToast();
  const navigate = useNavigate();
  const { userID, code, mode } = Route.useSearch();
  const [password, setPasswordValue] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const missing = !userID.trim() || !code.trim();

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (missing || busy) return;
    if (password.length < 8) {
      toastError(t("invitePasswordShort"));
      return;
    }
    // Zitadel default complexity: upper + lower + digit + symbol.
    if (
      !/[A-Z]/.test(password) ||
      !/[a-z]/.test(password) ||
      !/[0-9]/.test(password) ||
      !/[^A-Za-z0-9]/.test(password)
    ) {
      toastError(t("invitePasswordPolicy"));
      return;
    }
    if (password !== confirm) {
      toastError(t("invitePasswordMismatch"));
      return;
    }
    setBusy(true);
    try {
      const res = await setPassword({
        userId: userID,
        code,
        password,
        mode,
      });
      if (!res.ok) {
        if (res.error === "password_policy") {
          toastError(t("invitePasswordPolicy"));
        } else if (res.error === "invalid_code") {
          toastError(t("inviteInvalidCode"));
        } else {
          toastError(t("inviteFailed"));
        }
        return;
      }
      setDone(true);
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthCard title={mode === "reset" ? t("inviteResetTitle") : t("inviteTitle")}>
      {missing ? (
        <p className={authErrorClassName()}>{t("inviteMissingLink")}</p>
      ) : done ? (
        <div className="flex flex-col gap-5">
          <p className={authMutedClassName()}>{t("inviteSuccess")}</p>
          <Button
            type="button"
            variant="primary"
            size="lg"
            className="w-full"
            onClick={() => void navigate({ to: "/login" })}
          >
            {t("inviteGoLogin")}
          </Button>
        </div>
      ) : (
        <>
          <p className={authMutedClassName()}>
            {mode === "reset" ? t("inviteResetHint") : t("inviteHint")}
          </p>
          <form
            className={authFormClassName()}
            onSubmit={(e) => void onSubmit(e)}
          >
            <TextField
              size="lg"
              label={
                <>
                  {t("inviteNewPassword")}
                  <HintAsterisk ariaLabel={t("invitePasswordRulesAria")}>
                    {t("invitePasswordRules")}
                  </HintAsterisk>
                </>
              }
              type="password"
              showPasswordLabel={t("showPassword")}
              hidePasswordLabel={t("hidePassword")}
              autoComplete="new-password"
              value={password}
              onChange={(ev) => setPasswordValue(ev.target.value)}
              className={authFieldClassName()}
              required
              autoFocus
            />
            <TextField
              size="lg"
              label={t("inviteConfirmPassword")}
              type="password"
              showPasswordLabel={t("showPassword")}
              hidePasswordLabel={t("hidePassword")}
              autoComplete="new-password"
              value={confirm}
              onChange={(ev) => setConfirm(ev.target.value)}
              className={authFieldClassName()}
              required
            />
            <Button
              type="submit"
              variant="primary"
              size="lg"
              className="mt-1 w-full"
              loading={busy}
              disabled={password.length < 8 || confirm.length < 8}
            >
              {t("inviteSubmit")}
            </Button>
          </form>
        </>
      )}
    </AuthCard>
  );
}
